import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Clock } from 'lucide-react';

const MESSAGES = [
  'Restoring your session…',
  'Preparing your workspace…',
  'Loading dashboard…',
  'Fetching today\'s attendance…',
  'Syncing attendance data…',
  'Checking offline data…',
  'Loading analytics…',
  'Almost ready…',
];

export default function AppLoader() {
  const prefersReduced = useReducedMotion();
  const [msgIndex, setMsgIndex] = useState(0);
  const [msgVisible, setMsgVisible] = useState(true);

  useEffect(() => {
    if (prefersReduced) return;
    const interval = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % MESSAGES.length);
        setMsgVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, [prefersReduced]);

  return (
    <div className="app-loader">
      {/* Ambient background orbs */}
      {!prefersReduced && (
        <>
          <motion.div
            className="app-loader__orb app-loader__orb--1"
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="app-loader__orb app-loader__orb--2"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
        </>
      )}

      <motion.div
        className="app-loader__content"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Animated logo */}
        <motion.div
          className="app-loader__logo"
          animate={prefersReduced ? {} : {
            boxShadow: [
              '0 0 0 0px rgba(79,124,255,0)',
              '0 0 0 12px rgba(79,124,255,0.12)',
              '0 0 0 0px rgba(79,124,255,0)',
            ],
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <motion.div
            animate={prefersReduced ? {} : { rotate: [0, 6, -3, 0], scale: [1, 1.06, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Clock size={28} strokeWidth={1.5} />
          </motion.div>
        </motion.div>

        {/* App name */}
        <motion.h1
          className="app-loader__title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          Biometric Calculator
        </motion.h1>

        {/* Rotating status message */}
        <div className="app-loader__msg-wrap">
          <motion.p
            key={msgIndex}
            className="app-loader__msg"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: msgVisible ? 1 : 0, y: msgVisible ? 0 : -6 }}
            transition={{ duration: 0.25 }}
          >
            {MESSAGES[msgIndex]}
          </motion.p>
        </div>

        {/* Indeterminate progress bar */}
        <div className="app-loader__track" role="progressbar" aria-label="Loading">
          <motion.div
            className="app-loader__bar"
            animate={prefersReduced
              ? { scaleX: 1 }
              : { x: ['-100%', '200%'] }
            }
            transition={prefersReduced
              ? {}
              : { duration: 1.6, repeat: Infinity, ease: [0.4, 0, 0.6, 1] }
            }
          />
        </div>
      </motion.div>
    </div>
  );
}
